import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Custom hook to manage highlight effect after closing a modal/viewer.
 * Tracks the last viewed item ID and triggers a temporary highlight with fade effect.
 *
 * @param durationMs - Duration of the highlight in milliseconds (default: 2000)
 * @returns Object containing highlightedId, lastViewedIdRef, and triggerHighlight function
 */
export function useHighlightAfterClose(durationMs = 2000) {
  const [highlightedId, setHighlightedId] = useState<number | null>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastViewedIdRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const triggerHighlight = useCallback(() => {
    const id = lastViewedIdRef.current;
    setHighlightedId(id);
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setHighlightedId(null), durationMs);
  }, [durationMs]);

  return { highlightedId, lastViewedIdRef, triggerHighlight };
}
